import React from 'react'

interface SEOProps {
  title: string
  description: string
  keywords?: string[]
  ogImage?: string
  ogUrl?: string
  canonicalUrl?: string
}

const SITE_NAME = 'Angel Technology'
const SITE_ORIGIN = 'https://www.angeltechpo.com.cn'

/** Current path without hash or query string, e.g. "/category/knee-joint". */
function currentPath(): string {
  if (typeof window === 'undefined') return '/'
  const { pathname } = window.location
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/'
}

function absoluteUrl(path: string): string {
  return `${SITE_ORIGIN}${path === '/' ? '/' : path}`
}

const SEO = ({
  title,
  description,
  keywords = [],
  ogImage,
  ogUrl,
  canonicalUrl
}: SEOProps) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`

  React.useEffect(() => {
    document.title = fullTitle
  }, [fullTitle])

  React.useEffect(() => {
    const setMeta = (selector: string, attr: string, key: string, content: string) => {
      let meta = document.querySelector(selector)
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute(attr, key)
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    }

    const path = currentPath()
    const pageUrl = canonicalUrl || ogUrl || absoluteUrl(path)

    setMeta('meta[name="description"]', 'name', 'description', description)
    setMeta(
      'meta[name="keywords"]',
      'name',
      'keywords',
      Array.isArray(keywords) ? keywords.join(', ') : keywords
    )
    setMeta('meta[name="robots"]', 'name', 'robots', 'index, follow')

    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', canonicalUrl || pageUrl)

    const image = ogImage || `${SITE_ORIGIN}/images/og-cover.png`

    const ogTags: Array<[string, string]> = [
      ['og:type', 'website'],
      ['og:site_name', SITE_NAME],
      ['og:locale', 'en_US'],
      ['og:url', pageUrl],
      ['og:title', fullTitle],
      ['og:description', description],
      ['og:image', image],
      ['og:image:alt', title]
    ]

    ogTags.forEach(([property, content]) => {
      setMeta(`meta[property="${property}"]`, 'property', property, content)
    })

    const twitterTags: Array<[string, string]> = [
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', fullTitle],
      ['twitter:description', description],
      ['twitter:image', image]
    ]

    twitterTags.forEach(([name, content]) => {
      setMeta(`meta[name="${name}"]`, 'name', name, content)
    })
  }, [title, description, keywords, ogImage, ogUrl, canonicalUrl, fullTitle])

  return null
}

export default SEO
